import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { findCollectionFieldById, idFieldSelectionToFieldSelection, mergeFieldSelections, resolveFieldDisplayFieldSelection } from '#layers/data-model/public';
import { useFieldSelectionContexts } from './use-field-selection-contexts';

/**
 * Compute precise FieldSelection for a set of visible field IDs on a collection.
 *
 * For each field, delegates to `resolveFieldDisplayFieldSelection` which handles
 * both primitive and relation fields, including display block `fields()` resolution.
 */
export function useFieldSelectionForFieldIds(
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>,
	visibleFieldIds: MaybeRefOrGetter<GlobalFieldId[]>,
) {
	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	return computed<FieldSelection>(() => {
		const col = toValue(collection);
		const fieldIds = toValue(visibleFieldIds);
		if (!col || fieldIds.length === 0) return ['*'];

		const columnSelections: FieldSelection[] = fieldIds.map((fieldId) => {
			const field = findCollectionFieldById(col, fieldId);

			if (!field) return [];

			const idSelection = resolveFieldDisplayFieldSelection(field, col, fieldSelectionCtx);
			return idFieldSelectionToFieldSelection(idSelection, idCtx);
		});

		return mergeFieldSelections(...columnSelections);
	});
}
